/* eslint-disable @typescript-eslint/no-unsafe-call */
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef } from "react";
import { useIntercom } from "react-use-intercom";

// Helper function to apply disabled styles to docs-related buttons
const applyDisabledDocsStyles = (selectors: string[]) => {
  selectors.forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => {
      const element = el as HTMLElement;
      element.style.opacity = "0.5";
      element.style.pointerEvents = "none";
      element.style.cursor = "not-allowed";
    });
  });
};

export const usePostHogFeatureFlags = () => {
  const { showConversation, showNewMessage, showTicket, startSurvey } =
    useIntercom();
  const posthog = usePostHog();
  const mutationObserverRef = useRef<MutationObserver | null>(null);

  // Function to check and apply unable_docs_features flag
  const checkUnableDocsFeatures = useCallback(() => {
    if (!posthog) {
      return;
    }

    const isFlagActive = posthog.getFeatureFlag("unable_docs_features");
    const unableDocsPayload = posthog.getFeatureFlagPayload(
      "unable_docs_features"
    ) as
      | {
          selectors?: string[];
        }
      | undefined;

    // Clean up existing observer if flag changed
    if (mutationObserverRef.current) {
      mutationObserverRef.current.disconnect();
      mutationObserverRef.current = null;
    }

    // Check if boolean flag is active
    if (isFlagActive === true) {
      // Use payload selectors if provided, otherwise use default selectors
      const selectors = unableDocsPayload?.selectors ?? [];

      // Apply styles immediately to existing elements
      applyDisabledDocsStyles(selectors);

      // Create MutationObserver to handle dynamically added elements
      const observer = new MutationObserver(() => {
        applyDisabledDocsStyles(selectors);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      mutationObserverRef.current = observer;
    }
  }, [posthog]);

  useEffect(() => {
    if (!posthog) {
      return;
    }

    // Check flags immediately on mount
    checkUnableDocsFeatures();

    // Also listen for flag changes
    posthog.onFeatureFlags(() => {
      // --------- FLAG 1: hide-button ---------
      const hidePayload = posthog.getFeatureFlagPayload("hide-button") as {
        selector?: string;
      };

      if (hidePayload?.selector) {
        const els = document.querySelectorAll(hidePayload.selector);
        els.forEach((el) => {
          (el as HTMLElement).style.display = "none";
        });
      }

      // --------- FLAG 2: launch-intercom ---------
      const variant = posthog.getFeatureFlag("launch-intercom");
      const payload = posthog.getFeatureFlagPayload("launch-intercom") as {
        selector?: string;
        intercom_type?: string;
        intercom_id?: number;
      };
      if (!variant || !payload?.selector) return;

      const target = document.querySelector(payload.selector);
      if (!target) {
        return;
      }

      // Remove previous click handlers by cloning
      const clone = target.cloneNode(true) as HTMLElement;
      target.replaceWith(clone);

      if (variant === "intercom") {
        clone.addEventListener("click", () => {
          if (payload.intercom_type === "startSurvey" && payload.intercom_id) {
            startSurvey(payload.intercom_id);
          } else if (
            payload.intercom_type === "showConversation" &&
            payload.intercom_id
          ) {
            showConversation(payload.intercom_id);
          } else if (
            payload.intercom_type === "showTicket" &&
            payload.intercom_id
          ) {
            showTicket(payload.intercom_id);
          } else if (payload.intercom_type === "showNewMessage") {
            showNewMessage();
          } else {
            console.warn(
              "Unsupported or missing intercom_type for Intercom action"
            );
          }
        });
      }

      // --------- FLAG 3: Redirect onclick ---------
      const redirectPayload = posthog.getFeatureFlagPayload(
        "redirect-onclick"
      ) as {
        selector?: string;
        url: string;
        blank?: boolean;
      };
      if (redirectPayload?.selector) {
        const els = document.querySelectorAll(redirectPayload.selector);
        els.forEach((el) => {
          // Remove previous click handlers by cloning
          const clone = el.cloneNode(true) as HTMLElement;
          el.replaceWith(clone);
          clone.addEventListener("click", () => {
            if (redirectPayload.blank) {
              window.open(redirectPayload.url, "_blank");
            } else {
              window.location.href = redirectPayload.url;
            }
          });
        });
      }

      // --------- FLAG 4: unable_docs_features ---------
      checkUnableDocsFeatures();
    });
  }, [checkUnableDocsFeatures, posthog]);

  // Cleanup MutationObserver on unmount
  useEffect(() => {
    return () => {
      if (mutationObserverRef.current) {
        mutationObserverRef.current.disconnect();
        mutationObserverRef.current = null;
      }
    };
  }, []);
};
